import { useRef, useEffect, useCallback } from 'react';

// useResizeObserver handles the creation and clean up of a ResizeObserver.
// It returns a ref to provide to the components who's size is to be observed.
// The provided callback is called when the elment is initially rendered and on
// any future resize events. The callback is called with one argument, an object
// containing the width and height of the elements border box size.
//
// Example:
//
// ```
// function MyWidget(props) {
//   const ref = useResizeObserver(({ height, width }) => {
//     ...work the magic...
//   });
//
//   return <Component ref={ref} />
// }
// ```
//
// See:
// https://developer.mozilla.org/en-US/docs/Web/API/Resize_Observer_API
// https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry

const useResizeObserver = (onResize) => {
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  function unobserve() {
    if (observerRef.current && elementRef.current) {
      observerRef.current.unobserve(elementRef.current);
    }
  }

  useEffect(() => unobserve, []);

  function buildRef(element) {
    const box = element.getBoundingClientRect();
    return {
      current: element,
      // Safari only provides the content area without padding or borders so we
      // calculate the size from the element.
      height: (box.bottom - box.top),
      width: (box.right - box.left)
    };
  }

  return useCallback((element) => {
    if (!element) return;

    unobserve();

    elementRef.current = element;
    onResize(buildRef(element));

    observerRef.current = new ResizeObserver(() => onResize(buildRef(element)));
    observerRef.current.observe(element);
  }, []);
};

export default useResizeObserver;
